import { Link } from "react-router-dom";
import { MEDIA, CHURCH, REAL_PHOTOS } from "@/lib/church";
import { Quotes, EnvelopeSimple, Phone, ArrowRight, BookOpen, Heart, Users, Sparkle } from "@phosphor-icons/react";

export default function Pastor() {
    return (
        <div data-testid="pastor-page" className="bg-white">
            {/* HERO — bold split */}
            <section className="relative bg-[#4C1D95] text-white overflow-hidden">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end py-20 lg:py-24">
                    <div className="lg:col-span-7 relative z-10">
                        <p className="overline text-[#FFC72C] mb-4 animate-fade-up">Senior Pastor</p>
                        <h1 className="font-display text-6xl sm:text-7xl lg:text-7xl font-bold tracking-tight leading-[0.85] animate-fade-up animate-delay-100" data-testid="pastor-title">
                            Rev.<br />
                            Marlo<br />
                            <span className="text-[#FFC72C] italic">Woods</span>
                            <span className="text-white">,</span>{" "}
                            <span className="text-[#FFC72C] italic" data-testid="pastor-credential">M.Div.</span>
                        </h1>
                        <div className="mt-10 flex flex-col sm:flex-row gap-3 animate-fade-up animate-delay-200">
                            <a href={CHURCH.emailHref} data-testid="pastor-email" className="inline-flex items-center gap-2 bg-[#FFC72C] hover:bg-[#E6B327] text-[#2E1A47] px-6 py-4 text-xs font-black uppercase tracking-widest transition-colors">
                                <EnvelopeSimple size={14} weight="bold" /> Email Pastor
                            </a>
                            <a href={CHURCH.phoneHref} data-testid="pastor-phone" className="inline-flex items-center gap-2 border-2 border-white hover:bg-white hover:text-[#2E1A47] text-white px-6 py-4 text-xs font-black uppercase tracking-widest transition-colors">
                                <Phone size={14} weight="bold" /> Call the Church
                            </a>
                        </div>
                    </div>
                    <div className="lg:col-span-5 relative">
                        <div className="zoom-on-hover">
                            <img src={MEDIA.pastorReading} alt={CHURCH.pastor} className="w-full aspect-[4/5] object-cover" />
                        </div>
                        <div className="absolute -bottom-4 -left-4 bg-[#FFC72C] text-[#2E1A47] px-6 py-4 hidden sm:block">
                            <p className="overline">Greater Mount Olive AME</p>
                            <p className="font-display text-2xl font-bold tracking-tight">Since 1816 Tradition</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* QUOTE BANNER */}
            <section className="bg-[#FFC72C] text-[#2E1A47] py-20 lg:py-28 relative overflow-hidden">
                <Quotes size={300} weight="fill" className="absolute -left-12 -top-8 text-[#2E1A47]/10" />
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
                    <p className="overline mb-6">A Word from Pastor Woods</p>
                    <p className="font-display text-3xl sm:text-4xl lg:text-6xl font-bold tracking-tight leading-[1.0]">
                        "Ministry is not about a stage — it's about a <span className="italic">table</span>. Everyone has a seat."
                    </p>
                </div>
            </section>

            {/* BIO */}
            <section className="bg-white py-20 lg:py-28">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
                    <div className="lg:col-span-4 lg:sticky lg:top-32 self-start">
                        <p className="overline text-[#4C1D95] mb-4">About Pastor Woods</p>
                        <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-[#2E1A47] leading-[0.9]">
                            A shepherd who knows the sheep.
                        </h2>
                    </div>
                    <div className="lg:col-span-8 space-y-6 text-base lg:text-lg text-slate-600 leading-relaxed">
                        <p>Rev. Marlo Woods, M.Div., serves as Senior Pastor of Greater Mount Olive AME Church. An educated, experienced, and deeply compassionate leader, she brings depth of training and a steady, professional hand to every facet of pastoral ministry.</p>
                        <p>Pastor Woods earned her Master of Divinity and has devoted her career to proclaiming the gospel with clarity, scholarship, and care. Under her leadership, the congregation has grown in spiritual depth, community engagement, and intergenerational connection.</p>
                        <p>Her preaching joins the historic AME tradition with a contemporary voice — biblically grounded, culturally aware, and pastorally honest. She leads with the prophetic spirit of Bishop Richard Allen and the personal warmth of a shepherd who knows her sheep by name.</p>
                        <p>Beyond the pulpit, Pastor Woods serves as a Philadelphia Police Chaplain, mentors emerging leaders, and partners across the community — ensuring that Greater Mount Olive remains a hub of hope on North 50th Street.</p>
                        <div className="pt-4">
                            <img src={REAL_PHOTOS.PASTOR_POLICE} alt="Pastor Woods serving as Police Chaplain" className="w-full aspect-[4/3] object-cover" data-testid="pastor-chaplain-photo" />
                            <p className="mt-3 text-xs uppercase tracking-[0.2em] font-bold text-[#4C1D95]">Pastor Woods · Philadelphia Police Chaplain</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* PILLARS */}
            <section className="bg-[#F4F7F9] py-20 lg:py-28">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <p className="overline text-[#4C1D95] mb-4">Ministry Pillars</p>
                    <h2 className="font-display text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tight text-[#2E1A47] leading-[0.9] mb-12 max-w-3xl">
                        Faith. Justice. <span className="text-[#4C1D95]">Family.</span>
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {[
                            { icon: BookOpen, title: "Preaching", text: "Biblically grounded messages with cultural relevance for today's world." },
                            { icon: Users, title: "Discipleship", text: "Equipping believers to grow in their faith and walk together." },
                            { icon: Heart, title: "Outreach", text: "Loving our West Philadelphia neighbors through tangible action." },
                            { icon: Sparkle, title: "Vision", text: "A church reflecting the kingdom — diverse, joyful, and on mission." },
                        ].map((b, i) => (
                            <div key={b.title} className={`p-8 ${i === 1 ? 'bg-[#4C1D95] text-white' : i === 2 ? 'bg-[#FFC72C] text-[#2E1A47]' : 'bg-white text-[#2E1A47]'}`}>
                                <b.icon size={36} weight="duotone" className={`mb-5 ${i === 1 ? 'text-[#FFC72C]' : i === 2 ? 'text-[#2E1A47]' : 'text-[#4C1D95]'}`} />
                                <h3 className="font-display text-2xl font-bold tracking-tight mb-3">{b.title}</h3>
                                <p className={`text-sm leading-relaxed ${i === 1 ? 'text-white/85' : i === 2 ? 'text-[#2E1A47]/80' : 'text-slate-600'}`}>{b.text}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="bg-[#4C1D95] text-white py-20 lg:py-24">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h2 className="font-display text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tight leading-[0.9] mb-6">
                        Worship with us<br />
                        <span className="text-[#FFC72C] italic">this Sunday.</span>
                    </h2>
                    <p className="text-lg text-white/80 mb-10 max-w-2xl mx-auto">Meet Pastor Woods. Meet our family. Find your place at Greater Mount Olive.</p>
                    <div className="flex flex-wrap gap-3 justify-center">
                        <Link to="/contact#visit" data-testid="pastor-visit-cta" className="inline-flex items-center gap-2 bg-[#FFC72C] hover:bg-[#E6B327] text-[#2E1A47] px-8 py-4 text-xs font-black uppercase tracking-widest transition-colors">
                            Plan Your Visit <ArrowRight size={14} weight="bold" />
                        </Link>
                        <Link to="/sermons" data-testid="pastor-sermons-cta" className="inline-flex items-center gap-2 border-2 border-white hover:bg-white hover:text-[#2E1A47] text-white px-8 py-4 text-xs font-black uppercase tracking-widest transition-colors">
                            Hear Recent Sermons
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
